/**
 * Todo Store
 *
 * Zustand store for managing task lists and tasks.
 * Hydrated from Supabase via `load()` (no persistence).
 *
 * @module features/todo-list/stores/todoStore
 */

import { create } from 'zustand';
import { toast } from '@/shared/hooks/useToast';
import { isPersistedId } from '@/shared/lib/persistedId';
import { registerFamilyScopedReset } from '@/shared/lib/storeReset';
import { ToastVariant } from '@/shared/types/toast.types';
import { todoService } from '../services/todoService';
import type {
  CreateTaskInput,
  CreateTaskListInput,
  Task,
  TaskCategory,
  TaskList,
  TaskSortConfig,
  UpdateTaskInput,
  UpdateTaskListInput,
} from '../types/task.types';
import { calculateListStats } from '../utils/listStats';

// ============================================
// Types
// ============================================

interface TodoState {
  // Data
  readonly tasks: Task[];
  readonly taskLists: TaskList[];
  readonly categories: TaskCategory[];
  readonly selectedListId: string | null;
  readonly selectedTaskId: string | null;

  // UI State
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly isLoaded: boolean;
  readonly sortConfig: TaskSortConfig;

  // Modals
  readonly isCreateModalOpen: boolean;
  readonly editingTaskId: string | null;

  // Task Actions
  addTask: (input: CreateTaskInput) => Task;
  updateTask: (taskId: string, updates: Partial<UpdateTaskInput>) => void;
  deleteTask: (taskId: string) => void;
  /**
   * Merge a task received from a realtime stream (P7). Inserts when unseen,
   * replaces when the incoming row is newer than the newest SERVER-stamped row
   * the store has seen for that id (see {@link lastServerUpdatedAt}). Unlike
   * `addTask` it accepts a fully-formed `Task` (it does not generate a new id).
   */
  upsertRealtimeTask: (task: Task) => void;
  /**
   * Remove a task deleted on another device (P7). Local-only — does NOT call
   * the service, so a DELETE echo never re-issues to the server the delete
   * that produced it. Use {@link TodoState.deleteTask} for user-initiated
   * deletions.
   */
  deleteRealtimeTask: (taskId: string) => void;
  toggleTaskStatus: (taskId: string) => void;

  // Task List Actions
  addTaskList: (input: CreateTaskListInput) => TaskList;
  updateTaskList: (
    listId: string,
    updates: Partial<UpdateTaskListInput>
  ) => void;
  deleteTaskList: (listId: string) => void;
  archiveTaskList: (listId: string) => void;

  // Selection Actions
  setSelectedListId: (listId: string | null) => void;
  setSelectedTaskId: (taskId: string | null) => void;

  // Sort Actions
  setSortConfig: (config: TaskSortConfig) => void;

  // Modal Actions
  openCreateModal: () => void;
  closeCreateModal: () => void;
  openEditModal: (taskId: string) => void;

  // Utility
  getListStats: (listId: string) => ReturnType<typeof calculateListStats>;
  /**
   * Load task lists, tasks and categories from Supabase. Idempotent —
   * guarded by `isLoaded` / `isLoading` so it is safe to call from every
   * mount.
   */
  load: () => Promise<void>;
  /**
   * Force a fresh read from the backend, bypassing the `isLoaded` /
   * `isLoading` guards, and replace the store data with the result. Used for
   * catch-up after a realtime reconnect and on window focus/online events
   * (changes streamed while the channel was down are lost). Does not toggle
   * `isLoading` so background refreshes never flash loading skeletons.
   */
  refetch: () => Promise<void>;
  reset: () => void;
}

// ============================================
// Initial State
// ============================================

const initialSortConfig: TaskSortConfig = {
  field: 'dueDate',
  direction: 'asc',
};

const getInitialState = () => ({
  // The store starts empty and is hydrated by `load()` from Supabase.
  tasks: [] as Task[],
  taskLists: [] as TaskList[],
  categories: [] as TaskCategory[],
  selectedListId: null as string | null,
  selectedTaskId: null as string | null,
  isLoading: false,
  error: null as string | null,
  isLoaded: false,
  sortConfig: initialSortConfig,
  isCreateModalOpen: false,
  editingTaskId: null as string | null,
});

// ============================================
// Helper Functions
// ============================================

function generateId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

function isoNow(): string {
  return new Date().toISOString();
}

// Concurrency guard for background refetches (module-level — not rendered
// state, so it never causes loading-skeleton flashes).
let refetchInFlight = false;

/**
 * The newest SERVER-stamped `updatedAt` this session has seen per task id.
 *
 * ONE CLOCK, NOT TWO. Realtime dedup used to compare the incoming row's
 * `updatedAt` against whatever `updatedAt` the store happened to hold for that
 * id — and the store's value is very often a CLIENT stamp, because every
 * optimistic path writes `isoNow()` from the browser clock while the incoming
 * row carries the `updated_at` Postgres stamped through `set_updated_at`. The
 * two clocks are unrelated. A device a few seconds fast made
 * `existing.updatedAt >= task.updatedAt` true for a genuinely NEWER remote row,
 * and the echo was dropped with no log, no retry and no reconciliation until
 * the next full refetch — the other person's edit simply did not arrive, on the
 * device whose clock was wrong, for as long as the skew lasted.
 *
 * So the comparison now has server stamps on BOTH sides: this map is written
 * only where a row came from the service (`load`, `refetch`, the create
 * reconciliation, and an applied realtime row) and never by an optimistic
 * write. When there is no entry for an id, the server row wins — a realtime row
 * for a task the store has no server stamp for is by definition the more
 * authoritative of the two.
 *
 * Bounded by the tasks one session has seen and cleared by `reset()`, which the
 * family-scope reset below also drives.
 */
const lastServerUpdatedAt = new Map<string, string>();

/** Record the server stamp of rows that came from the service. */
function rememberServerTasks(tasks: readonly Task[]): void {
  for (const task of tasks) {
    lastServerUpdatedAt.set(task.id, task.updatedAt);
  }
}

/**
 * Persist a store mutation to the backend. Fire-and-forget with error
 * surfacing: on failure the optional `rollback` reverts the optimistic state
 * (so no ghost data lingers that other family members can never see), the
 * failure lands in `state.error` and the user gets a destructive toast.
 */
function persistTodoMutation(
  fallbackMessage: string,
  run: () => Promise<void>,
  rollback?: () => void
): void {
  void run().catch((error: unknown) => {
    rollback?.();
    const message = error instanceof Error ? error.message : fallbackMessage;
    useTodoStore.setState({ error: message });
    toast({
      variant: ToastVariant.Destructive,
      title: 'Грешка при запазване',
      description: message,
    });
  });
}

/**
 * Swap an optimistic task for its persisted counterpart (id reconciliation).
 * Also drops a possible realtime echo of the same DB row so it never
 * duplicates the reconciled entity.
 */
function reconcileTask(optimisticId: string, persisted: Task): void {
  rememberServerTasks([persisted]);
  useTodoStore.setState((state) => ({
    tasks: state.tasks
      .filter((t) => t.id !== persisted.id || t.id === optimisticId)
      .map((t) => (t.id === optimisticId ? persisted : t)),
    selectedTaskId:
      state.selectedTaskId === optimisticId
        ? persisted.id
        : state.selectedTaskId,
    editingTaskId:
      state.editingTaskId === optimisticId ? persisted.id : state.editingTaskId,
  }));
}

/**
 * Resolve one optional list field against an update.
 *
 * THE THREE STATES ARE ALL REAL, and `updates.x ?? list.x` can only express
 * two of them. `undefined` means the edit did not carry the field, so whatever
 * the list already has survives; `null` means the user cleared it (the emoji
 * picker has a clear button), so it must go; a string replaces it. Folding
 * `null` into "keep" is how a cleared emoji comes straight back on the next
 * render, which looks exactly like the save being ignored.
 *
 * The return type is the entity's, not the input's: `TaskList.emoji` is
 * `string | undefined`, so a cleared field leaves the object entirely rather
 * than sitting on it as `null`.
 */
function resolveOptionalListField(
  update: string | null | undefined,
  current: string | undefined
): string | undefined {
  if (update === undefined) return current;
  return update ?? undefined;
}

/** Swap an optimistic task list for its persisted counterpart. */
function reconcileTaskList(optimisticId: string, persisted: TaskList): void {
  useTodoStore.setState((state) => ({
    taskLists: state.taskLists
      .filter((l) => l.id !== persisted.id || l.id === optimisticId)
      .map((l) => (l.id === optimisticId ? persisted : l)),
    tasks: state.tasks.map((t) =>
      t.listId === optimisticId ? { ...t, listId: persisted.id } : t
    ),
    selectedListId:
      state.selectedListId === optimisticId
        ? persisted.id
        : state.selectedListId,
  }));
}

// ============================================
// Store
// ============================================

export const useTodoStore = create<TodoState>((set, get) => ({
  ...getInitialState(),

  // ============================================
  // Task Actions
  // ============================================

  addTask: (input: CreateTaskInput) => {
    const now = isoNow();
    const tasks = get().tasks;

    // Calculate max order for the list
    const listTasks = tasks.filter((t) => t.listId === input.listId);
    const maxOrder =
      listTasks.length > 0 ? Math.max(...listTasks.map((t) => t.order)) : -1;

    // Build task using conditional spread for optional properties
    const newTask: Task = {
      id: generateId('task'),
      listId: input.listId,
      title: input.title,
      status: 'pending',
      priority: input.priority ?? 'medium',
      createdBy: input.createdBy,
      assignees: input.assignees ?? [],
      order: maxOrder + 1,
      createdAt: now,
      updatedAt: now,
      ...(input.description !== undefined && {
        description: input.description,
      }),
      ...(input.emoji !== undefined && { emoji: input.emoji }),
      ...(input.categoryId !== undefined && { categoryId: input.categoryId }),
      ...(input.dueDate !== undefined && { dueDate: input.dueDate }),
      ...(input.dueTime !== undefined && { dueTime: input.dueTime }),
      ...(input.reminderMinutes !== undefined && {
        reminderMinutes: input.reminderMinutes,
      }),
      ...(input.recurrence !== undefined && { recurrence: input.recurrence }),
    };

    set((state) => ({
      tasks: [...state.tasks, newTask],
    }));

    // Write-through: persist and reconcile the optimistic id with the DB row.
    // On failure the optimistic task is removed again (rollback).
    persistTodoMutation(
      'Задачата не можа да бъде запазена.',
      async () => {
        const persisted = await todoService.createTask(input);
        reconcileTask(newTask.id, persisted);
      },
      () => {
        useTodoStore.setState((state) => ({
          tasks: state.tasks.filter((t) => t.id !== newTask.id),
          selectedTaskId:
            state.selectedTaskId === newTask.id ? null : state.selectedTaskId,
          editingTaskId:
            state.editingTaskId === newTask.id ? null : state.editingTaskId,
        }));
      }
    );

    return newTask;
  },

  updateTask: (taskId: string, updates: Partial<UpdateTaskInput>) => {
    const previousTask = get().tasks.find((t) => t.id === taskId);

    set((state) => ({
      tasks: state.tasks.map((task): Task => {
        if (task.id !== taskId) return task;

        // Build updated task using conditional spread
        return {
          id: task.id,
          listId: task.listId,
          title: updates.title ?? task.title,
          status: updates.status ?? task.status,
          priority: updates.priority ?? task.priority,
          createdBy: task.createdBy,
          assignees: updates.assignees ?? task.assignees,
          order: task.order,
          createdAt: task.createdAt,
          updatedAt: isoNow(),
          // Preserve/update optional properties
          ...(task.eventId !== undefined && { eventId: task.eventId }),
          ...((updates.description !== undefined ||
            task.description !== undefined) && {
            description: updates.description ?? task.description,
          }),
          ...((updates.emoji !== undefined || task.emoji !== undefined) && {
            emoji: updates.emoji ?? task.emoji,
          }),
          ...((updates.categoryId !== undefined ||
            task.categoryId !== undefined) && {
            categoryId: updates.categoryId ?? task.categoryId,
          }),
          ...((updates.dueDate !== undefined || task.dueDate !== undefined) && {
            dueDate: updates.dueDate ?? task.dueDate,
          }),
          ...((updates.dueTime !== undefined || task.dueTime !== undefined) && {
            dueTime: updates.dueTime ?? task.dueTime,
          }),
          ...(task.recurrence !== undefined && { recurrence: task.recurrence }),
          ...((updates.reminderMinutes !== undefined ||
            task.reminderMinutes !== undefined) && {
            reminderMinutes: updates.reminderMinutes ?? task.reminderMinutes,
          }),
          ...(task.completedAt !== undefined && {
            completedAt: task.completedAt,
          }),
          ...(task.completedBy !== undefined && {
            completedBy: task.completedBy,
          }),
        };
      }),
    }));

    if (isPersistedId(taskId)) {
      persistTodoMutation(
        'Промяната не можа да бъде запазена.',
        async () => {
          await todoService.updateTask(taskId, updates);
        },
        () => {
          if (!previousTask) return;
          useTodoStore.setState((state) => ({
            tasks: state.tasks.map((t) => (t.id === taskId ? previousTask : t)),
          }));
        }
      );
    }
  },

  deleteTask: (taskId: string) => {
    const deletedTask = get().tasks.find((t) => t.id === taskId);

    set((state) => ({
      tasks: state.tasks.filter((task) => task.id !== taskId),
      selectedTaskId:
        state.selectedTaskId === taskId ? null : state.selectedTaskId,
      editingTaskId:
        state.editingTaskId === taskId ? null : state.editingTaskId,
    }));

    if (isPersistedId(taskId)) {
      persistTodoMutation(
        'Изтриването не можа да бъде запазено.',
        async () => {
          await todoService.deleteTask(taskId);
        },
        () => {
          if (!deletedTask) return;
          useTodoStore.setState((state) =>
            state.tasks.some((t) => t.id === taskId)
              ? {}
              : { tasks: [...state.tasks, deletedTask] }
          );
        }
      );
    }
  },

  upsertRealtimeTask: (task: Task) => {
    // Both sides of this comparison are Postgres stamps — see
    // `lastServerUpdatedAt`. What is dropped here is a re-delivery or an
    // out-of-order replay of a row the store already has; an optimistic local
    // edit is NOT a reason to refuse a server row, because the server row is
    // the authoritative one and our own write echoes back a moment later
    // anyway, carrying a stamp newer than the one being applied now.
    const lastSeen = lastServerUpdatedAt.get(task.id);
    if (lastSeen !== undefined && task.updatedAt <= lastSeen) {
      return;
    }
    lastServerUpdatedAt.set(task.id, task.updatedAt);

    set((state) => {
      const exists = state.tasks.some((t) => t.id === task.id);
      return exists
        ? { tasks: state.tasks.map((t) => (t.id === task.id ? task : t)) }
        : { tasks: [...state.tasks, task] };
    });
  },

  deleteRealtimeTask: (taskId: string) => {
    // The stamp is deliberately KEPT. It is what stops a re-delivered UPDATE
    // echo, older than the DELETE that just landed, from re-inserting the row
    // the other device deleted. The entry costs one short string per task and
    // goes away with `reset()`.
    set((state) => ({
      tasks: state.tasks.filter((task) => task.id !== taskId),
      selectedTaskId:
        state.selectedTaskId === taskId ? null : state.selectedTaskId,
      editingTaskId:
        state.editingTaskId === taskId ? null : state.editingTaskId,
    }));
  },

  toggleTaskStatus: (taskId: string) => {
    const task = get().tasks.find((t) => t.id === taskId);
    if (!task) return;
    const previousTask = task;

    const now = isoNow();
    const isCompleting = task.status !== 'completed';

    set((state) => ({
      tasks: state.tasks.map((t): Task => {
        if (t.id !== taskId) return t;

        // Build updated task using conditional spread
        return {
          id: t.id,
          listId: t.listId,
          title: t.title,
          status: isCompleting ? 'completed' : 'pending',
          priority: t.priority,
          createdBy: t.createdBy,
          assignees: t.assignees,
          order: t.order,
          createdAt: t.createdAt,
          updatedAt: now,
          // Preserve existing optional properties
          ...(t.eventId !== undefined && { eventId: t.eventId }),
          ...(t.description !== undefined && { description: t.description }),
          ...(t.emoji !== undefined && { emoji: t.emoji }),
          ...(t.categoryId !== undefined && { categoryId: t.categoryId }),
          ...(t.dueDate !== undefined && { dueDate: t.dueDate }),
          ...(t.dueTime !== undefined && { dueTime: t.dueTime }),
          ...(t.recurrence !== undefined && { recurrence: t.recurrence }),
          ...(t.reminderMinutes !== undefined && {
            reminderMinutes: t.reminderMinutes,
          }),
          // Add completion fields only when completing
          ...(isCompleting && { completedAt: now, completedBy: t.createdBy }),
        };
      }),
    }));

    if (isPersistedId(taskId)) {
      persistTodoMutation(
        'Статусът не можа да бъде запазен.',
        async () => {
          await todoService.toggleTaskStatus(taskId);
        },
        () => {
          useTodoStore.setState((state) => ({
            tasks: state.tasks.map((t) => (t.id === taskId ? previousTask : t)),
          }));
        }
      );
    }
  },

  // NO `reorderTask` HERE, DELIBERATELY. There was one: it rewrote `order` on
  // the tapped task and shifted its neighbours, entirely in memory. Nothing
  // called it — no component, hook, page or test — and nothing could have made
  // it stick, because `ITodoService` has no reorder method and the action never
  // went near `persistTodoMutation`. A drag would have reordered the list until
  // the next refetch replaced `tasks` from the server and put it back.
  //
  // Ordering is a real feature and it is not this: it needs a service method
  // that writes `tasks.sort_order` for every shifted row in one statement, plus
  // a drag handle. Until that exists, `sort_order` is read-only to this store.

  // ============================================
  // Task List Actions
  // ============================================

  addTaskList: (input: CreateTaskListInput) => {
    const now = isoNow();

    // Build list using conditional spread for optional properties.
    //
    // This object and the one `reconcileTaskList` swaps in for it a moment
    // later must describe the same list, because the swap is wholesale: every
    // field the optimistic object has and the persisted one lacks is a field
    // the user sees appear and then disappear. `emoji` and `color` are exactly
    // that story — they had no columns, so `rowToTaskList` could not return
    // them and the tile reverted to the grey default on every single create.
    // With migration 028 the two shapes agree; the only fields that still
    // differ are ones nothing renders (`createdBy`, which the service
    // re-resolves to the real `owner_id` because `input.createdBy` may be the
    // `member-vladi` modal default, and the server timestamps).
    //
    // `!= null` rather than `!== undefined`: a cleared emoji arrives as `null`
    // and must leave the entity without the key, not carry a null on it.
    const newList: TaskList = {
      id: generateId('list'),
      title: input.title,
      isArchived: false,
      createdBy: input.createdBy,
      memberIds: input.memberIds ?? [],
      createdAt: now,
      updatedAt: now,
      ...(input.description !== undefined && {
        description: input.description,
      }),
      ...(input.emoji != null && { emoji: input.emoji }),
      ...(input.color != null && { color: input.color }),
    };

    set((state) => ({
      taskLists: [...state.taskLists, newList],
    }));

    persistTodoMutation(
      'Списъкът не можа да бъде запазен.',
      async () => {
        const persisted = await todoService.createTaskList(input);
        reconcileTaskList(newList.id, persisted);
      },
      () => {
        useTodoStore.setState((state) => ({
          taskLists: state.taskLists.filter((l) => l.id !== newList.id),
          selectedListId:
            state.selectedListId === newList.id ? null : state.selectedListId,
        }));
      }
    );

    return newList;
  },

  updateTaskList: (listId: string, updates: Partial<UpdateTaskListInput>) => {
    const previousList = get().taskLists.find((l) => l.id === listId);

    set((state) => ({
      taskLists: state.taskLists.map((list): TaskList => {
        if (list.id !== listId) return list;

        // Optional properties are merged through `resolveOptionalListField`,
        // which keeps "not part of this edit" and "cleared by the user" apart.
        // The service applies the same three states to the row, so what the
        // tile shows now is what the next `load()` will read back.
        const description = resolveOptionalListField(
          updates.description,
          list.description
        );
        const emoji = resolveOptionalListField(updates.emoji, list.emoji);
        const color = resolveOptionalListField(updates.color, list.color);

        // Build updated list using conditional spread
        return {
          id: list.id,
          title: updates.title ?? list.title,
          isArchived: updates.isArchived ?? list.isArchived,
          createdBy: list.createdBy,
          memberIds: updates.memberIds ?? list.memberIds,
          createdAt: list.createdAt,
          updatedAt: isoNow(),
          // Preserve/update optional properties
          ...(list.calendarId !== undefined && { calendarId: list.calendarId }),
          ...(description !== undefined && { description }),
          ...(emoji !== undefined && { emoji }),
          ...(color !== undefined && { color }),
        };
      }),
    }));

    if (isPersistedId(listId)) {
      persistTodoMutation(
        'Промяната не можа да бъде запазена.',
        async () => {
          await todoService.updateTaskList(listId, updates);
        },
        () => {
          if (!previousList) return;
          useTodoStore.setState((state) => ({
            taskLists: state.taskLists.map((l) =>
              l.id === listId ? previousList : l
            ),
          }));
        }
      );
    }
  },

  deleteTaskList: (listId: string) => {
    const deletedList = get().taskLists.find((l) => l.id === listId);
    const deletedTasks = get().tasks.filter((t) => t.listId === listId);

    set((state) => ({
      taskLists: state.taskLists.filter((list) => list.id !== listId),
      tasks: state.tasks.filter((task) => task.listId !== listId),
      selectedListId:
        state.selectedListId === listId ? null : state.selectedListId,
    }));

    if (isPersistedId(listId)) {
      persistTodoMutation(
        'Изтриването не можа да бъде запазено.',
        async () => {
          await todoService.deleteTaskList(listId);
        },
        () => {
          if (!deletedList) return;
          useTodoStore.setState((state) => ({
            taskLists: state.taskLists.some((l) => l.id === listId)
              ? state.taskLists
              : [...state.taskLists, deletedList],
            tasks: [
              ...state.tasks,
              ...deletedTasks.filter(
                (dt) => !state.tasks.some((t) => t.id === dt.id)
              ),
            ],
          }));
        }
      );
    }
  },

  archiveTaskList: (listId: string) => {
    const previousList = get().taskLists.find((l) => l.id === listId);

    set((state) => ({
      taskLists: state.taskLists.map(
        (list): TaskList =>
          list.id === listId
            ? { ...list, isArchived: true, updatedAt: isoNow() }
            : list
      ),
    }));

    if (isPersistedId(listId)) {
      persistTodoMutation(
        'Архивирането не можа да бъде запазено.',
        async () => {
          await todoService.archiveTaskList(listId);
        },
        () => {
          if (!previousList) return;
          useTodoStore.setState((state) => ({
            taskLists: state.taskLists.map((l) =>
              l.id === listId ? previousList : l
            ),
          }));
        }
      );
    }
  },

  // ============================================
  // Selection Actions
  // ============================================

  setSelectedListId: (listId: string | null) => {
    set({
      selectedListId: listId,
      selectedTaskId: null,
    });
  },

  setSelectedTaskId: (taskId: string | null) => {
    set({ selectedTaskId: taskId });
  },

  // ============================================
  // Sort Actions
  // ============================================

  setSortConfig: (config: TaskSortConfig) => {
    set({ sortConfig: config });
  },

  // ============================================
  // Modal Actions
  // ============================================

  openCreateModal: () => {
    set({ isCreateModalOpen: true, editingTaskId: null });
  },

  closeCreateModal: () => {
    set({ isCreateModalOpen: false, editingTaskId: null });
  },

  openEditModal: (taskId: string) => {
    set({
      editingTaskId: taskId,
      isCreateModalOpen: true,
    });
  },

  // ============================================
  // Utility
  // ============================================

  getListStats: (listId: string) => {
    return calculateListStats(get().tasks, listId);
  },

  load: async () => {
    const { isLoaded, isLoading } = get();
    if (isLoaded || isLoading) return;

    set({ isLoading: true, error: null });
    try {
      const [taskLists, tasks, categories] = await Promise.all([
        todoService.getTaskLists(),
        todoService.getTasks(),
        todoService.getCategories(),
      ]);
      rememberServerTasks(tasks);
      set({
        taskLists,
        tasks,
        categories,
        isLoaded: true,
        isLoading: false,
      });
    } catch (error) {
      set({
        error:
          error instanceof Error
            ? error.message
            : 'Грешка при зареждане на задачите',
        isLoading: false,
      });
    }
  },

  refetch: async () => {
    if (refetchInFlight) return;
    refetchInFlight = true;
    try {
      const [taskLists, tasks, categories] = await Promise.all([
        todoService.getTaskLists(),
        todoService.getTasks(),
        todoService.getCategories(),
      ]);
      rememberServerTasks(tasks);
      set({
        taskLists,
        tasks,
        categories,
        isLoaded: true,
        error: null,
      });
    } catch (error) {
      set({
        error:
          error instanceof Error
            ? error.message
            : 'Грешка при зареждане на задачите',
      });
    } finally {
      refetchInFlight = false;
    }
  },

  reset: () => {
    // The realtime bookkeeping is family-scoped like the data it guards: the
    // next family's rows must not be measured against this one's stamps.
    lastServerUpdatedAt.clear();
    set(getInitialState());
  },
}));

// ============================================
// Selectors
// ============================================

export const selectTasks = (state: TodoState) => state.tasks;
export const selectTaskLists = (state: TodoState) => state.taskLists;
export const selectActiveLists = (state: TodoState) =>
  state.taskLists.filter((l) => !l.isArchived);
export const selectSelectedTask = (state: TodoState) =>
  state.selectedTaskId
    ? state.tasks.find((t) => t.id === state.selectedTaskId)
    : null;
export const selectSelectedList = (state: TodoState) =>
  state.selectedListId
    ? state.taskLists.find((l) => l.id === state.selectedListId)
    : null;
export const selectCategories = (state: TodoState) => state.categories;

// Clear task data whenever the active family changes so the next family
// never sees the previous scope's lists/tasks.
registerFamilyScopedReset(() => {
  useTodoStore.getState().reset();
});
